import { useCallback, useEffect, useMemo, useState } from "react";
import { AdminButton, AdminCard, AdminErrorBanner, AdminFormField, AdminPageLayout, AdminSelect, AdminTextarea, AdminTextInput } from "@/components/admin";
import {
  cancelAdminCollectionSchedulePolicy,
  createAdminCollectionSchedulePolicy,
  fetchAdminCollectionSchedulePolicyCurrent,
  fetchAdminCollectionSchedulePolicyHistory,
  fetchMe,
  fetchSystemVersion,
  reconcileMissingSoundSubjects,
  type AdminCollectionSchedulePolicy,
  type AdminCollectionScheduleRule,
  type AdminSoundSubjectReconcileItem,
} from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import type { MeResponse, SystemVersionResponse } from "@/types.ts";
import SummaryCards from "./components/SummaryCards";
import SettingsPanel from "./components/SettingsPanel";
import CurrentSummaryCard from "./components/CurrentSummaryCard";
import ChangeHistoryCard from "./components/ChangeHistoryCard";
import InfoCard from "./components/InfoCard";

export type AdminSettingsStatus = {
  admin: MeResponse | null;
  version: SystemVersionResponse | null;
};

const SettingsPage = () => {
  const [admin, setAdmin] = useState<MeResponse | null>(null);
  const [version, setVersion] = useState<SystemVersionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [subjectIds, setSubjectIds] = useState("");
  const [subjectReasonCode, setSubjectReasonCode] = useState("DATA_CORRECTION");
  const [subjectReason, setSubjectReason] = useState("");
  const [subjectConfirmation, setSubjectConfirmation] = useState("");
  const [subjectSaving, setSubjectSaving] = useState(false);
  const [subjectResults, setSubjectResults] = useState<AdminSoundSubjectReconcileItem[]>([]);
  const [collectionCurrent, setCollectionCurrent] = useState<{ active: AdminCollectionSchedulePolicy | null; scheduled: AdminCollectionSchedulePolicy | null } | null>(null);
  const [collectionHistory, setCollectionHistory] = useState<AdminCollectionSchedulePolicy[]>([]);
  const [policyCancelReasonCode, setPolicyCancelReasonCode] = useState("OPERATOR_REQUEST");
  const [policyCancelReason, setPolicyCancelReason] = useState("");
  const [policyCancelConfirmation, setPolicyCancelConfirmation] = useState("");
  const [policyCancelling, setPolicyCancelling] = useState(false);
  const [policyMutationMessage, setPolicyMutationMessage] = useState("");
  const [policyCreateOpen, setPolicyCreateOpen] = useState(false);
  const [policyEffectiveAt, setPolicyEffectiveAt] = useState("");
  const [policyChangeReason, setPolicyChangeReason] = useState("");
  const [policyCreateReasonCode, setPolicyCreateReasonCode] = useState("OPERATOR_REQUEST");
  const [policyCreateReason, setPolicyCreateReason] = useState("");
  const [policyCreateConfirmation, setPolicyCreateConfirmation] = useState("");
  const [policyDraftRules, setPolicyDraftRules] = useState<AdminCollectionScheduleRule[]>([]);
  const [policyCreating, setPolicyCreating] = useState(false);

  const parsedSubjectIds = useMemo(() => Array.from(new Set(subjectIds
    .split(/[\s,]+/)
    .map((value) => Number(value.trim()))
    .filter((value) => Number.isSafeInteger(value) && value > 0))), [subjectIds]);
  const subjectConfirmationText = `RECONCILE:${parsedSubjectIds.length}`;

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const [me, systemVersion, currentPolicy, policyHistory] = await Promise.all([
        fetchMe(),
        fetchSystemVersion(),
        fetchAdminCollectionSchedulePolicyCurrent(),
        fetchAdminCollectionSchedulePolicyHistory(),
      ]);
      setAdmin(me);
      setVersion(systemVersion);
      setCollectionCurrent(currentPolicy);
      setCollectionHistory(policyHistory);
    } catch (error) {
      setAdmin(null);
      setVersion(null);
      setCollectionCurrent(null);
      setCollectionHistory([]);
      setErrorMessage(error instanceof Error ? error.message : "운영 설정 상태를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const status = useMemo<AdminSettingsStatus>(() => ({
    admin,
    version,
  }), [admin, version]);

  const handleSubjectReconcile = async () => {
    if (parsedSubjectIds.length === 0 || parsedSubjectIds.length > 100 || subjectReason.trim().length < 10 || subjectConfirmation !== subjectConfirmationText) return;
    setSubjectSaving(true);
    setErrorMessage(null);
    try {
      const response = await reconcileMissingSoundSubjects({
        userAccountIds: parsedSubjectIds,
        reasonCode: subjectReasonCode,
        reason: subjectReason.trim(),
        confirmationText: subjectConfirmation,
      });
      setSubjectResults(response.items);
      setSubjectConfirmation("");
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "소리 subject 보정에 실패했습니다.");
    } finally {
      setSubjectSaving(false);
    }
  };

  const handlePolicyCancel = async () => {
    const scheduled = collectionCurrent?.scheduled;
    if (!scheduled || policyCancelReason.trim().length < 10 || policyCancelConfirmation !== `CANCEL:${scheduled.schedulePolicyVersion}`) return;
    setPolicyCancelling(true);
    setErrorMessage(null);
    try {
      const result = await cancelAdminCollectionSchedulePolicy(scheduled.schedulePolicyVersion, {
        reasonCode: policyCancelReasonCode,
        reason: policyCancelReason.trim(),
        confirmationText: policyCancelConfirmation,
      });
      setPolicyMutationMessage(`예약 정책 v${result.policy.schedulePolicyVersion}을 취소했습니다. 감사 로그 #${result.audit.auditLogId}`);
      setPolicyCancelReason("");
      setPolicyCancelConfirmation("");
      await loadStatus();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "예약 수집정책 취소에 실패했습니다.");
    } finally {
      setPolicyCancelling(false);
    }
  };

  const openPolicyCreate = () => {
    if (!collectionCurrent?.active) return;
    setPolicyDraftRules(collectionCurrent.active.rules.map((rule) => ({ ...rule })));
    setPolicyEffectiveAt("");
    setPolicyChangeReason("");
    setPolicyCreateReason("");
    setPolicyCreateConfirmation("");
    setPolicyCreateOpen(true);
  };

  const updatePolicyRuleNumber = (ruleSequenceNo: number, key: "intervalMinutes" | "soundCaptureDurationSec" | "locationAcquisitionWindowSec" | "locationAcceptedAccuracyMeter", value: string) => {
    setPolicyDraftRules((current) => current.map((rule) => rule.ruleSequenceNo === ruleSequenceNo
      ? { ...rule, [key]: value === "" ? null : Number(value) }
      : rule));
  };

  const policyCreateConfirmationText = collectionCurrent?.active
    ? `CREATE:${collectionCurrent.active.schedulePolicyVersion}:${collectionCurrent.scheduled?.schedulePolicyVersion ?? "NONE"}`
    : "CREATE:-:NONE";

  const handlePolicyCreate = async () => {
    const active = collectionCurrent?.active;
    if (!active || !policyEffectiveAt.trim() || policyChangeReason.trim().length < 10 || policyCreateReason.trim().length < 10 || policyCreateConfirmation !== policyCreateConfirmationText) return;
    setPolicyCreating(true);
    setErrorMessage(null);
    try {
      const result = await createAdminCollectionSchedulePolicy({
        policy: {
          expectedActivePolicyVersion: active.schedulePolicyVersion,
          expectedScheduledPolicyVersion: collectionCurrent?.scheduled?.schedulePolicyVersion,
          effectiveAt: policyEffectiveAt.trim(),
          timeZone: "Asia/Seoul",
          changeReason: policyChangeReason.trim(),
          rules: policyDraftRules.map(({ schedulePolicyRuleId: _ignored, ...rule }) => rule),
        },
        reasonCode: policyCreateReasonCode,
        reason: policyCreateReason.trim(),
        confirmationText: policyCreateConfirmation,
      });
      setPolicyMutationMessage(`수집정책 v${result.policy.schedulePolicyVersion} 예약을 등록했습니다. 감사 로그 #${result.audit.auditLogId}`);
      setPolicyCreateOpen(false);
      await loadStatus();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "수집정책 예약 생성에 실패했습니다.");
    } finally {
      setPolicyCreating(false);
    }
  };

  return (
    <AdminPageLayout
      title="시스템 운영 정보"
      subtitle="현재 관리자 권한과 실행 중인 API 릴리즈 정보"
      actions={(
        <AdminButton variant="primary" size="sm" icon={loading ? "ri-refresh-line animate-spin" : "ri-refresh-line"} onClick={loadStatus} disabled={loading}>
          새로고침
        </AdminButton>
      )}
    >
      <div className="mb-4">
        <AdminErrorBanner message={errorMessage} />
      </div>

      <section className="mb-4">
        <SummaryCards status={status} loading={loading} />
      </section>

      <AdminCard className="mb-4 p-4">
        <h2 className="text-sm font-bold text-slate-800">누락 소리 Subject 보정</h2>
        <p className="mt-1 text-xs text-slate-500">Senior 계정 ID를 최대 100개 입력합니다. 기존 subject는 재사용하고, 모든 자격 조건을 충족한 누락 계정만 생성합니다.</p>
        <div className="mt-3 grid gap-2 lg:grid-cols-2">
          <AdminTextarea value={subjectIds} onChange={(event) => { setSubjectIds(event.target.value); setSubjectResults([]); }} disabled={subjectSaving} rows={3} placeholder="Senior 계정 ID (쉼표 또는 줄바꿈 구분)" />
          <div className="space-y-2">
            <AdminSelect value={subjectReasonCode} onValueChange={setSubjectReasonCode} disabled={subjectSaving}><option value="OPERATOR_REQUEST">운영 요청</option><option value="DATA_CORRECTION">데이터 교정</option><option value="SECURITY_RESPONSE">보안 대응</option><option value="TEST_ROLLBACK">테스트·원복 검증</option><option value="OTHER">기타</option></AdminSelect>
            <AdminTextarea value={subjectReason} onChange={(event) => setSubjectReason(event.target.value)} disabled={subjectSaving} maxLength={500} rows={2} placeholder="보정 사유를 10자 이상 입력하세요." />
            <AdminTextInput tone="danger" value={subjectConfirmation} onChange={(event) => setSubjectConfirmation(event.target.value)} disabled={subjectSaving} placeholder={`확인 문구 입력: ${subjectConfirmationText}`} />
            <AdminButton variant="danger" size="sm" disabled={subjectSaving || parsedSubjectIds.length === 0 || parsedSubjectIds.length > 100 || subjectReason.trim().length < 10 || subjectConfirmation !== subjectConfirmationText} onClick={() => void handleSubjectReconcile()}>{subjectSaving ? "보정 중..." : `${parsedSubjectIds.length}개 계정 보정`}</AdminButton>
          </div>
        </div>
        {subjectResults.length > 0 && <div className="mt-3 space-y-1 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs">{subjectResults.map((item) => <p key={item.userAccountId}><span className="font-semibold">#{item.userAccountId}</span> · {item.disposition} · subject {item.soundDataSubjectId ?? "-"}{item.audit ? ` · 감사 #${item.audit.auditLogId}` : ""}{item.missingConditionCodes.length ? ` · ${item.missingConditionCodes.join(", ")}` : ""}{item.errorCode ? ` · ${item.errorCode}` : ""}</p>)}</div>}
      </AdminCard>

      <AdminCard className="mb-4 p-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between"><div><h2 className="text-sm font-bold text-slate-800">수집 스케줄 정책 v3</h2><p className="text-xs text-slate-500">조회 시 예약 정책을 자동 승격하지 않습니다. 신규 정책은 미래 시행 예약만 허용합니다.</p></div><AdminButton variant="soft" size="sm" disabled={!collectionCurrent?.active || policyCreating} onClick={openPolicyCreate}>활성 정책 복제 예약</AdminButton></div>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          {[collectionCurrent?.active, collectionCurrent?.scheduled].map((policy, index) => <div key={index === 0 ? "active" : "scheduled"} className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs">
            <p className="font-bold text-slate-700">{index === 0 ? "현재 활성" : "예약"}</p>
            {policy ? <div className="mt-2 space-y-1 text-slate-600"><p>버전 {policy.schedulePolicyVersion} · {policy.status}</p><p>시행 {formatDateTime(policy.effectiveAt)}</p><p>규칙 {policy.rules.length}개 · {policy.timeZone}</p><p className="break-all text-xs text-slate-400">{policy.changeReason}</p></div> : <p className="mt-2 text-slate-400">정책 없음</p>}
          </div>)}
        </div>
        <div className="mt-3 space-y-1">{collectionHistory.slice(0, 10).map((policy) => <div key={policy.schedulePolicyId} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2 text-xs"><span className="font-semibold text-slate-700">v{policy.schedulePolicyVersion} · {policy.status}</span><span className="text-slate-500">{formatDateTime(policy.effectiveAt)} · 규칙 {policy.rules.length}개</span></div>)}{collectionHistory.length === 0 && <p className="text-xs text-slate-400">정책 이력이 없습니다.</p>}</div>
        {policyMutationMessage && <p className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs font-medium text-emerald-700">{policyMutationMessage}</p>}
        {collectionCurrent?.scheduled && <div className="mt-3 space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          <p className="font-semibold">예약 정책 v{collectionCurrent.scheduled.schedulePolicyVersion} 취소</p>
          <AdminSelect tone="warning" value={policyCancelReasonCode} onValueChange={setPolicyCancelReasonCode} disabled={policyCancelling}><option value="OPERATOR_REQUEST">운영 요청</option><option value="DATA_CORRECTION">데이터 교정</option><option value="SECURITY_RESPONSE">보안 대응</option><option value="TEST_ROLLBACK">테스트·원복 검증</option><option value="OTHER">기타</option></AdminSelect>
          <AdminTextarea tone="warning" value={policyCancelReason} onChange={(event) => setPolicyCancelReason(event.target.value)} disabled={policyCancelling} maxLength={500} rows={3} placeholder="취소 사유를 10자 이상 입력하세요." />
          <AdminTextInput tone="danger" value={policyCancelConfirmation} onChange={(event) => setPolicyCancelConfirmation(event.target.value)} disabled={policyCancelling} placeholder={`확인 문구 입력: CANCEL:${collectionCurrent.scheduled.schedulePolicyVersion}`} />
          <AdminButton variant="danger" size="sm" disabled={policyCancelling || policyCancelReason.trim().length < 10 || policyCancelConfirmation !== `CANCEL:${collectionCurrent.scheduled.schedulePolicyVersion}`} onClick={() => void handlePolicyCancel()}>{policyCancelling ? "취소 중..." : "예약 정책 취소"}</AdminButton>
        </div>}
        {policyCreateOpen && collectionCurrent?.active && <div className="mt-3 space-y-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-slate-700">
          <p className="font-semibold">활성 v{collectionCurrent.active.schedulePolicyVersion} 기준 미래 예약</p>
          <AdminTextInput tone="info" value={policyEffectiveAt} onChange={(event) => setPolicyEffectiveAt(event.target.value)} disabled={policyCreating} placeholder="API 시행시각 그대로 입력 (예: 2026-08-28T09:00:00+09:00)" />
          <AdminTextarea tone="info" value={policyChangeReason} onChange={(event) => setPolicyChangeReason(event.target.value)} disabled={policyCreating} maxLength={500} rows={2} placeholder="정책 변경 내용을 10자 이상 입력하세요." />
          <div className="space-y-2">
            {policyDraftRules.map((rule) => {
              const fieldPrefix = `policy-rule-${rule.ruleSequenceNo}`;
              return (
                <div key={rule.ruleSequenceNo} className="grid gap-2 rounded-lg border border-blue-100 bg-white p-2 sm:grid-cols-2 lg:grid-cols-4">
                  <p className="font-semibold">#{rule.ruleSequenceNo} {rule.targetCode}</p>
                  <AdminFormField label="간격(분)" htmlFor={`${fieldPrefix}-interval`}>
                    <AdminTextInput id={`${fieldPrefix}-interval`} type="number" min={1} value={rule.intervalMinutes} onChange={(event) => updatePolicyRuleNumber(rule.ruleSequenceNo, "intervalMinutes", event.target.value)} controlSize="sm" />
                  </AdminFormField>
                  {rule.targetCode === "SOUND" && (
                    <AdminFormField label="녹음(초)" htmlFor={`${fieldPrefix}-capture-duration`}>
                      <AdminTextInput id={`${fieldPrefix}-capture-duration`} type="number" min={1} value={rule.soundCaptureDurationSec ?? ""} onChange={(event) => updatePolicyRuleNumber(rule.ruleSequenceNo, "soundCaptureDurationSec", event.target.value)} controlSize="sm" />
                    </AdminFormField>
                  )}
                  {rule.targetCode === "LOCATION" && (
                    <>
                      <AdminFormField label="수집창(초)" htmlFor={`${fieldPrefix}-acquisition-window`}>
                        <AdminTextInput id={`${fieldPrefix}-acquisition-window`} type="number" min={1} value={rule.locationAcquisitionWindowSec ?? ""} onChange={(event) => updatePolicyRuleNumber(rule.ruleSequenceNo, "locationAcquisitionWindowSec", event.target.value)} controlSize="sm" />
                      </AdminFormField>
                      <AdminFormField label="정확도(m)" htmlFor={`${fieldPrefix}-accepted-accuracy`}>
                        <AdminTextInput id={`${fieldPrefix}-accepted-accuracy`} type="number" min={1} value={rule.locationAcceptedAccuracyMeter ?? ""} onChange={(event) => updatePolicyRuleNumber(rule.ruleSequenceNo, "locationAcceptedAccuracyMeter", event.target.value)} controlSize="sm" />
                      </AdminFormField>
                    </>
                  )}
                </div>
              );
            })}
          </div>
          <AdminSelect tone="info" value={policyCreateReasonCode} onValueChange={setPolicyCreateReasonCode} disabled={policyCreating}><option value="OPERATOR_REQUEST">운영 요청</option><option value="DATA_CORRECTION">데이터 교정</option><option value="SECURITY_RESPONSE">보안 대응</option><option value="TEST_ROLLBACK">테스트·원복 검증</option><option value="OTHER">기타</option></AdminSelect>
          <AdminTextarea tone="info" value={policyCreateReason} onChange={(event) => setPolicyCreateReason(event.target.value)} disabled={policyCreating} maxLength={500} rows={2} placeholder="실행 사유를 10자 이상 입력하세요." />
          <AdminTextInput tone="danger" value={policyCreateConfirmation} onChange={(event) => setPolicyCreateConfirmation(event.target.value)} disabled={policyCreating} placeholder={`확인 문구 입력: ${policyCreateConfirmationText}`} />
          <div className="flex gap-2"><AdminButton variant="primary" size="sm" disabled={policyCreating || !policyEffectiveAt.trim() || policyChangeReason.trim().length < 10 || policyCreateReason.trim().length < 10 || policyCreateConfirmation !== policyCreateConfirmationText} onClick={() => void handlePolicyCreate()}>{policyCreating ? "예약 중..." : "정책 예약 생성"}</AdminButton><AdminButton variant="ghost" size="sm" disabled={policyCreating} onClick={() => setPolicyCreateOpen(false)}>닫기</AdminButton></div>
        </div>}
      </AdminCard>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
        <div className="xl:col-span-3">
          <SettingsPanel status={status} loading={loading} />
        </div>

        <div className="xl:col-span-1 flex flex-col gap-4">
          <CurrentSummaryCard status={status} />
          <ChangeHistoryCard version={version} />
          <InfoCard />
        </div>
      </div>
    </AdminPageLayout>
  );
};

export default SettingsPage;
